/**
 * ClockStatusBar — Persistent dashboard widget with 3 states:
 * 1. Not clocked in: full-width forest-700 "Clock In" button
 * 2. Clocked in: compact white bar with timer, break button, clock out
 * 3. On break: amber bar with break timer and "End Break" button
 */

import { useState, useEffect, useCallback } from 'react';
import {
  useMyTimesheet,
  useClockIn,
  useClockOut,
  useBreakAction,
} from '../../../hooks/useTimesheets';
import { captureGpsOrNull } from '../utils/captureGpsOrNull';
import { GpsDisclosure } from './GpsDisclosure';

/** Check if GPS disclosure has been acknowledged */
function hasGpsDisclosure(): boolean {
  try { return localStorage.getItem('twg:time:gpsDisclosureAcknowledged') === 'true'; } catch { return false; }
}

/** Normalize API errors to safe, capped user messages */
function toUserError(err: unknown): string {
  const raw = err instanceof Error ? err.message : 'Something went wrong';
  return raw.length > 120 ? 'An unexpected error occurred. Please try again.' : raw;
}

interface ClockStatusBarProps {
  /** Called after a successful clock-in */
  onClockIn?: () => void;
  /** Called after a successful clock-out */
  onClockOut?: () => void;
}

/** Returns today's date in Pacific time as YYYY-MM-DD */
function getTodayPacific(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
}

export function ClockStatusBar({ onClockIn, onClockOut }: ClockStatusBarProps) {
  const today = getTodayPacific();
  const { data: timeRecord, isLoading } = useMyTimesheet(today);
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const breakAction = useBreakAction();

  const [gpsLoading, setGpsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const [showGpsDisclosure, setShowGpsDisclosure] = useState(false);

  // Timer: re-render every 60s for elapsed display
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  // Auto-dismiss error after 5s
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  const handleClockIn = useCallback(async () => {
    // Show GPS disclosure on first clock-in attempt
    if (!hasGpsDisclosure()) {
      setShowGpsDisclosure(true);
      return;
    }
    setGpsLoading(true);
    const gps = await captureGpsOrNull();
    setGpsLoading(false);
    clockIn.mutate(gps ?? undefined, {
      onSuccess: () => onClockIn?.(),
      onError: (err) => setError(toUserError(err)),
    });
  }, [clockIn, onClockIn]);

  const handleClockOut = useCallback(async () => {
    setGpsLoading(true);
    const gps = await captureGpsOrNull();
    setGpsLoading(false);
    clockOut.mutate(gps ?? undefined, {
      onSuccess: () => onClockOut?.(),
      onError: (err) => setError(toUserError(err)),
    });
  }, [clockOut, onClockOut]);

  const handleBreakStart = useCallback(() => {
    breakAction.mutate({ action: 'start', type: 'lunch' }, {
      onError: (err) => setError(toUserError(err)),
    });
  }, [breakAction]);

  const handleBreakEnd = useCallback(() => {
    breakAction.mutate({ action: 'end' }, {
      onError: (err) => setError(toUserError(err)),
    });
  }, [breakAction]);

  const isMutating = clockIn.isPending || clockOut.isPending || breakAction.isPending || gpsLoading;

  // GPS disclosure overlay (shown on first clock-in attempt)
  const gpsDisclosureEl = showGpsDisclosure ? (
    <GpsDisclosure onAcknowledge={() => { setShowGpsDisclosure(false); handleClockIn(); }} />
  ) : null;

  // Loading skeleton
  if (isLoading) {
    return <div className="h-14 bg-sage-100 rounded-2xl animate-pulse" />;
  }

  const segments = timeRecord?.segments ?? [];
  const breaks = timeRecord?.breaks ?? [];
  const openSegment = segments.find(s => s.clockOut === null);
  const activeBreak = breaks.find(b => !b.endTime);

  // --- State 3: On Break ---
  if (openSegment && activeBreak) {
    const breakStartMs = new Date(activeBreak.startTime).getTime();
    const breakElapsedMin = Math.max(0, Math.round((Date.now() - breakStartMs) / 60_000));

    return (
      <div className="relative">
        <div className="flex items-center justify-between px-4 py-3 bg-amber-100 border border-amber-300 rounded-2xl">
          <div className="flex items-center gap-2">
            {/* Pulsing dot */}
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" />
            </span>
            <span className="text-amber-800 font-medium text-sm">
              On break &mdash; {breakElapsedMin}m
            </span>
          </div>
          <button
            onClick={handleBreakEnd}
            disabled={isMutating}
            className="min-h-touch px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold active:scale-95 transition-all disabled:opacity-50"
          >
            End Break
          </button>
        </div>
        {error && <p className="text-red-600 text-xs mt-1 px-2">{error}</p>}
      </div>
    );
  }

  // --- State 2: Clocked In ---
  if (openSegment) {
    const clockedInMs = new Date(openSegment.clockIn).getTime();
    const elapsedMin = Math.max(0, Math.round((Date.now() - clockedInMs) / 60_000));
    const hours = Math.floor(elapsedMin / 60);
    const mins = elapsedMin % 60;
    const elapsedLabel = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

    return (
      <div className="relative">
        <div className="flex items-center justify-between px-4 py-3 bg-white border border-sage-200 rounded-2xl shadow-sm">
          {/* Elapsed timer */}
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-forest-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="text-forest-900 font-medium text-sm">{elapsedLabel}</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Break button (lunch bag icon) */}
            <button
              onClick={handleBreakStart}
              disabled={isMutating}
              className="min-h-touch p-2 rounded-xl border border-sage-200 hover:bg-sage-50 active:scale-95 transition-all disabled:opacity-50"
              aria-label="Start break"
            >
              <svg className="w-5 h-5 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M3 3h18v4H3zM5 7v10a4 4 0 004 4h6a4 4 0 004-4V7" />
                <path d="M8 7v2a4 4 0 004 4v0a4 4 0 004-4V7" />
              </svg>
            </button>

            {/* Clock Out button */}
            <button
              onClick={handleClockOut}
              disabled={isMutating}
              className="min-h-touch px-4 py-2 rounded-xl bg-earth-500 hover:bg-earth-600 text-white text-sm font-semibold active:scale-95 transition-all disabled:opacity-50"
            >
              Clock Out
            </button>
          </div>
        </div>
        {error && <p className="text-red-600 text-xs mt-1 px-2">{error}</p>}
      </div>
    );
  }

  // --- State 1: Not Clocked In ---
  return (
    <div className="relative">
      {gpsDisclosureEl}
      <button
        onClick={handleClockIn}
        disabled={isMutating}
        className="w-full min-h-touch flex items-center justify-center gap-3 px-6 py-4 bg-forest-700 hover:bg-forest-800 text-white rounded-2xl shadow-md active:scale-95 transition-all disabled:opacity-50"
      >
        {/* GPS pin icon */}
        <svg className="w-5 h-5 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
        <div className="flex flex-col items-start">
          <span className="font-display font-bold text-lg leading-tight">Clock In</span>
          <span className="text-white/70 text-xs">Start your day</span>
        </div>
      </button>
      {error && <p className="text-red-600 text-xs mt-1 px-2">{error}</p>}
    </div>
  );
}
